import { computed, useContext } from '@nuxtjs/composition-api';

import { useLink } from '~/diptyqueTheme/composable';
import type { HeadingData, UseFlexibleSectionInterface, VisualData } from '~/diptyqueTheme/composable/useFlexibleSection/types';
import type { FlexibleSectionModel } from '~/diptyqueTheme/types/contentful';

export const useFlexibleSection = (
  sectionData: FlexibleSectionModel,
  defaultRatio: {
    mobile: string | null;
    desktop: string | null;
  }
): UseFlexibleSectionInterface => {
  const { normalizeLink } = useLink();
  const { app } = useContext();
  const { isDesktop } = app.$device;
  const isFlexibleSection = computed<boolean>(() => sectionData.__typename === 'Flexible');
  const visualData = computed<VisualData>(() => {
    const { visual } = sectionData;

    return {
      source: 'contentful',
      src: visual?.vis?.url,
      srcDesktop: visual?.visD?.url,
      type: visual?.vis?.contentType?.split('/')[0],
      typeDesktop: visual?.visD?.contentType?.split('/')[0],
      link: normalizeLink(visual?.link),
      autoplay: visual?.vidSet?.includes('Autoplay') || false,
      muted: visual?.vidSet?.includes('Muted') || false,
      loop: visual?.vidSet?.includes('Loop') || false,
      pos: visual?.pos,
      posD: visual?.posD,
      ratio: visual?.ratio,
      ratioD: visual?.ratioD,
      srcAdaptive: isDesktop && visual?.visD?.url ? visual?.visD?.url : visual?.vis?.url,
      visible: !!visual?.vis?.url || !!(isDesktop && visual?.visD?.url)
    };
  });

  const headingData = computed<HeadingData>(() => {
    return {
      heading: sectionData?.heading,
      headingLink: sectionData?.headingLink,
      description: sectionData?.descr,
      subHeading: sectionData?.subHeading
    };
  });

  /**
   * Checks if the provided ratio is a valid string in the format "X:Y".
   * Invalid ratios will default to the predefined values (e.g. "Default for current module").
   * @param ratio - The ratio string to validate
   * @returns {boolean} - Returns true if the ratio is valid, false otherwise
   */
  const isValidRatio = (ratio: string | undefined): boolean => {
    return typeof ratio === 'string' && /^\d+:\d+$/.test(ratio);
  };

  /**
   * Computes the aspect ratio for mobile and desktop based on the section data.
   * If the ratio is invalid, it will return null. So that the fallback value can be provided.
   */
  const aspectRatio = computed(() => ({
    mobile: isValidRatio(visualData.value.ratio) ? visualData.value.ratio : defaultRatio.mobile,
    desktop: isValidRatio(visualData.value.ratioD) ? visualData.value.ratioD : defaultRatio.desktop
  }));

  /**
   * Predefined parallax aspect ratios  .
   * These are used to determine if the section should have a parallax effect.
   */
  const PARALLAX_RATIO: string[] = ['136:41', '144:41'];

  /**
   * Determines if the section has a parallax effect based on the desktop aspect ratio.
   */
  const hasParallax = computed(() => ({
    mobile: PARALLAX_RATIO.includes(aspectRatio.value.mobile),
    desktop: PARALLAX_RATIO.includes(aspectRatio.value.desktop)
  }));

  return {
    isValidRatio,
    aspectRatio,
    hasParallax,
    isFlexibleSection,
    headingData,
    visualData
  };
};
